<template>
  <v-container>
    <v-card>
      <v-card-title>
        Cuentas de correo
        <v-spacer />
      </v-card-title>
      <v-card-text>
        <div class="body-2 grey--text mb-2">
          Una cuenta es el nombre y la dirección con los que sale el correo. Las
          credenciales son las mismas para todas; la dirección tiene que estar
          verificada como remitente en SendGrid, si no el correo se rechaza.
        </div>

        <v-row align="center">
          <v-col cols="12" md="4">
            <v-text-field v-model="newAccount.name" label="Nombre" @keyup.enter="add" />
          </v-col>
          <v-col cols="12" md="5">
            <v-text-field v-model="newAccount.address" label="Dirección" type="email" @keyup.enter="add" />
          </v-col>
          <v-col cols="12" md="3">
            <v-btn class="primary" :disabled="!canAdd || saving" @click="add">Agregar</v-btn>
          </v-col>
        </v-row>

        <v-alert v-if="errorMessage" type="error" dense text>{{ errorMessage }}</v-alert>

        <v-data-table
          :headers="headers"
          :items="accounts"
          :loading="loading"
          no-data-text="No hay cuentas cargadas"
          :footer-props="{ itemsPerPageOptions: [15, 50, 100], itemsPerPageText: 'Mostrar' }"
        >
          <template v-slot:item.actions="{ item }">
            <v-btn icon :disabled="saving" @click="startEdit(item)">
              <v-icon color="primary">mode_edit</v-icon>
            </v-btn>
            <v-btn icon :disabled="saving" @click="remove(item)">
              <v-icon color="error">delete</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <v-dialog v-model="editDialog" max-width="600" persistent>
      <v-card v-if="editing">
        <v-card-title>Editar cuenta</v-card-title>
        <v-card-text>
          <v-text-field v-model="editing.name" label="Nombre" @keyup.enter="saveEdit" />
          <v-text-field v-model="editing.address" label="Dirección" type="email" @keyup.enter="saveEdit" />
          <v-alert v-if="editErrorMessage" type="error" dense text>{{ editErrorMessage }}</v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn :disabled="saving" @click="cancelEdit">Cancelar</v-btn>
          <v-btn class="primary" :loading="saving" :disabled="!editing.name || !editing.address" @click="saveEdit">Guardar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
"use strict";

import emailAccountService from "@/services/EmailAccountService";

// Mismo patron que list-tags.view.vue: alta en linea, tabla, edicion en dialogo.
export default {
  name: "list-email-accounts",
  data() {
    return {
      accounts: [],
      loading: false,
      // Bandera "en vuelo": sin esto, un doble click dispara dos altas.
      saving: false,
      newAccount: { name: "", address: "" },
      errorMessage: null,
      editDialog: false,
      editing: null,
      editErrorMessage: null,
      headers: [
        { text: "Nombre", value: "name" },
        { text: "Dirección", value: "address" },
        { text: "Acciones", value: "actions", sortable: false, width: 120 }
      ]
    };
  },
  computed: {
    canAdd() {
      return !!(this.newAccount.name && this.newAccount.address);
    }
  },
  async mounted() {
    await this.load();
  },
  methods: {
    async load() {
      this.errorMessage = null;
      this.loading = true;
      try {
        this.accounts = await emailAccountService.list();
      } catch (error) {
        this.errorMessage = this.messageOf(error, "No se pudieron cargar las cuentas");
      } finally {
        this.loading = false;
      }
    },
    // El backend manda el motivo real (direccion repetida, por ejemplo). Se
    // muestra tal cual y solo se cae al texto propio si no vino ninguno.
    messageOf(error, fallback) {
      return (error.response && error.response.data && error.response.data.message) || fallback;
    },
    async add() {
      if (!this.canAdd || this.saving) return;
      this.errorMessage = null;
      this.saving = true;
      try {
        await emailAccountService.create(this.newAccount);
        this.newAccount = { name: "", address: "" };
        await this.load();
      } catch (error) {
        this.errorMessage = this.messageOf(error, "No se pudo agregar la cuenta");
      } finally {
        this.saving = false;
      }
    },
    startEdit(item) {
      this.editing = { _id: item._id, name: item.name, address: item.address };
      this.editErrorMessage = null;
      this.editDialog = true;
    },
    cancelEdit() {
      this.editDialog = false;
      this.editing = null;
      this.editErrorMessage = null;
    },
    async saveEdit() {
      if (!this.editing || !this.editing.name || !this.editing.address || this.saving) return;
      this.editErrorMessage = null;
      this.saving = true;
      try {
        await emailAccountService.edit(this.editing._id, { name: this.editing.name, address: this.editing.address });
        this.cancelEdit();
        await this.load();
      } catch (error) {
        this.editErrorMessage = this.messageOf(error, "No se pudo editar la cuenta");
      } finally {
        this.saving = false;
      }
    },
    async remove(item) {
      if (!window.confirm(`¿Borrar la cuenta "${item.name}"? Las plantillas que la usan vuelven al remitente por defecto.`)) return;
      this.errorMessage = null;
      this.saving = true;
      try {
        await emailAccountService.remove(item._id);
        await this.load();
      } catch (error) {
        this.errorMessage = this.messageOf(error, "No se pudo borrar la cuenta");
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>
